import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { FiChevronsLeft, FiKey, FiLogOut, FiMenu } from "react-icons/fi";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import logo from "@assets/logo.png";
import { clearAuthSession, type LoginMenu } from "@features/auth";
import { routePaths } from "@config/routes";
import {
  isMenuPathActive,
  menuHref,
  menuLabel,
  menuTrailLabels,
  sortMenuParents,
  sortMenus,
} from "@/shared/navigation/menuTrail";
import {
  isMenuItemEnabled,
  MENU_DISABLED_TITLE,
} from "@/shared/navigation/menuAccess";
import { MenuParentIcon } from "@/shared/navigation/menuParentIcon";
import { useSelectedCompany } from "@shared/session/companySelection";
import { HeaderAccentRail } from "@features/welcome/components/Breadcrumbs";

function MenuChildIcon() {
  return (
    <span className="menu-child__icon" aria-hidden>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        className="bi bi-arrow-return-right"
        viewBox="0 0 16 16"
      >
        <path
          fillRule="evenodd"
          d="M1.5 1.5A.5.5 0 0 0 1 2v4.8a2.5 2.5 0 0 0 2.5 2.5h9.793l-3.347 3.346a.5.5 0 0 0 .708.708l4.2-4.2a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 8.3H3.5A1.5 1.5 0 0 1 2 6.8V2a.5.5 0 0 0-.5-.5"
        />
      </svg>
    </span>
  );
}

function handleToggleKey(e: KeyboardEvent, onToggle: () => void) {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onToggle();
  }
}

function normalizeMenus(menus: LoginMenu[]): LoginMenu[] {
  return menus.map((item) => ({
    ...item,
    children: normalizeMenus(item.children ?? []),
  }));
}

export interface SidebarProps {
  menus: LoginMenu[];
}

interface MenuChildListProps {
  items: LoginMenu[];
  nested?: boolean;
  expandedId: number | null;
  onToggle: (id: number) => void;
  currentPath: string;
  onNavigate: () => void;
  hasCompanySelected: boolean;
}

function MenuChildList({
  items,
  nested = false,
  expandedId,
  onToggle,
  currentPath,
  onNavigate,
  hasCompanySelected,
}: MenuChildListProps) {
  const sorted = sortMenus(items);
  const listClass = nested
    ? "menu-children-list menu-children-list--nested"
    : "menu-children-list";

  return (
    <ul className={listClass}>
      {sorted.map((item) => {
        const children = item.children ?? [];
        const hasChildren = children.length > 0;
        const label = menuLabel(item);

        if (hasChildren) {
          const isExpanded = expandedId === item.id;
          return (
            <li key={item.id} className="menu-children-list__group">
              <button
                type="button"
                className={`menu-subgroup-title menu-subgroup-title--toggle${isExpanded ? " active" : ""}`}
                aria-expanded={isExpanded}
                onClick={() => onToggle(item.id)}
                onKeyDown={(e) => handleToggleKey(e, () => onToggle(item.id))}
              >
                <span className="menu-subgroup-label">{label}</span>
                <span className="menu-arrow" aria-hidden>
                  ▼
                </span>
              </button>
              {isExpanded && (
                <MenuChildList
                  items={children}
                  nested
                  expandedId={expandedId}
                  onToggle={onToggle}
                  currentPath={currentPath}
                  onNavigate={onNavigate}
                  hasCompanySelected={hasCompanySelected}
                />
              )}
            </li>
          );
        }

        const enabled = isMenuItemEnabled(item, hasCompanySelected);
        const isActive = isMenuPathActive(currentPath, item.pageName);

        if (!enabled) {
          return (
            <li key={item.id}>
              <span
                className="menu-child menu-child--disabled"
                aria-disabled="true"
                title={MENU_DISABLED_TITLE}
              >
                <MenuChildIcon />
                <span className="menu-child-label">{label}</span>
              </span>
            </li>
          );
        }

        return (
          <li key={item.id}>
            <NavLink
              className={({ isActive: navActive }) =>
                `menu-child${navActive || isActive ? " active" : ""}`
              }
              to={menuHref(item.pageName)}
              onClick={onNavigate}
            >
              <MenuChildIcon />
              <span className="menu-child-label">{label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

interface MenuGroupProps {
  item: LoginMenu;
  isExpanded: boolean;
  onToggle: (id: number) => void;
  expandedId: number | null;
  currentPath: string;
  onNavigate: () => void;
  hasCompanySelected: boolean;
}

function MenuGroup({
  item,
  isExpanded,
  onToggle,
  expandedId,
  currentPath,
  onNavigate,
  hasCompanySelected,
}: MenuGroupProps) {
  const children = item.children ?? [];
  const label = menuLabel(item);
  const linkActive =
    children.length === 0 && isMenuPathActive(currentPath, item.pageName);

  if (children.length === 0) {
    const enabled = isMenuItemEnabled(item, hasCompanySelected);

    if (!enabled) {
      return (
        <span
          className="menu-parent menu-parent--link menu-parent--disabled"
          aria-disabled="true"
          title={MENU_DISABLED_TITLE}
        >
          <MenuParentIcon item={item} />
          <span className="menu-title">{label}</span>
        </span>
      );
    }

    return (
      <NavLink
        className={`menu-parent menu-parent--link${linkActive ? " active" : ""}`}
        to={menuHref(item.pageName)}
        onClick={onNavigate}
      >
        <MenuParentIcon item={item} />
        <span className="menu-title">{label}</span>
      </NavLink>
    );
  }

  return (
    <section className="menu-group">
      <button
        type="button"
        className={`menu-parent menu-parent--toggle${isExpanded ? " active" : ""}`}
        aria-expanded={isExpanded}
        onClick={() => onToggle(item.id)}
        onKeyDown={(e) => handleToggleKey(e, () => onToggle(item.id))}
      >
        <MenuParentIcon item={item} />
        <span className="menu-title">{label}</span>
        <span className="menu-arrow" aria-hidden>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-chevron-compact-down"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M1.553 6.776a.5.5 0 0 1 .67-.223L8 9.44l5.776-2.888a.5.5 0 1 1 .448.894l-6 3a.5.5 0 0 1-.448 0l-6-3a.5.5 0 0 1-.223-.67"
            />
          </svg>
        </span>
      </button>
      {isExpanded && (
        <MenuChildList
          items={children}
          expandedId={expandedId}
          onToggle={onToggle}
          currentPath={currentPath}
          onNavigate={onNavigate}
          hasCompanySelected={hasCompanySelected}
        />
      )}
    </section>
  );
}

export default function SidebarLayout({ menus }: SidebarProps) {
  const location = useLocation();
  // A floating sidebar also renders a full-page click-capturing overlay.
  // Start collapsed when the authenticated shell mounts after login.
  const [open, setOpen] = useState(false);
  const [dispass, setDispass] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isinCopied, setIsinCopied] = useState(false);
  const isinCopiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onToggle = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const closeSidebar = useCallback(() => {
    setOpen(false);
  }, []);

  const skipInitialPathClose = useRef(true);
  useEffect(() => {
    debugger;
    if([1, 99].includes(Number(usertype)))
        setDispass(true);
    if (skipInitialPathClose.current) {
      skipInitialPathClose.current = false;
      return;
    }
    setOpen(false);
  }, [location.pathname]);

  const handleMainPanelClick = useCallback(() => {
    if (open) {
      setOpen(false);
    }
  }, [open]);

  const sortedMenus = sortMenuParents(normalizeMenus(menus));
  const hasMenus = sortedMenus.length > 0;
  const { companyId, companyName, usertype } = useSelectedCompany();
  const sessionUserType = localStorage.getItem("UserType") ?? usertype;
  const hasCompanySelected =
    Boolean(companyId) || !["1", "3", "4", "99"].includes(sessionUserType);

  useEffect(() => {
    if (!hasCompanySelected) {
      setExpandedId(null);
    }
  }, [hasCompanySelected]);
  const navRailItems = useMemo(
    () => menuTrailLabels(sortedMenus, location.pathname),
    [sortedMenus, location.pathname],
  );
  const handleLogout = useCallback(() => {
    clearAuthSession();
    window.location.href = routePaths.login;
  }, []);

  const copyCompanyIsin = useCallback(async () => {
    if (!companyId) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(companyId);
      } else {
        const ta = document.createElement("textarea");
        ta.value = companyId;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setIsinCopied(true);
      if (isinCopiedTimer.current) window.clearTimeout(isinCopiedTimer.current);
      isinCopiedTimer.current = setTimeout(() => setIsinCopied(false), 2000);
    } catch {
      setIsinCopied(false);
    }
  }, [companyId]);

  useEffect(() => {
    setIsinCopied(false);
    return () => {
      if (isinCopiedTimer.current) window.clearTimeout(isinCopiedTimer.current);
    };
  }, [companyId]);

  return (
    <div
      className={`app-layout-shell${
        hasMenus
          ? open
            ? " app-layout-shell--sidebar-open"
            : " app-layout-shell--sidebar-collapsed"
          : ""
      }`}
    >
      <div className="app-chrome-top">
        <nav
          className="app-layout__subbar app-layout__subbar--unified shrink-0"
          aria-label="Page header"
        >
          <div
            className={`app-header-bar app-header-bar--pills${
              hasCompanySelected ? "" : " app-header-bar--no-company"
            }`}
          >
            <div className="app-header-bar__cluster app-header-bar__cluster--brand">
              <div className="app-header-bar__split app-header-bar__split--logo">
                <Link
                  to={routePaths.welcome}
                  className="app-header-bar__brand"
                  title="Home"
                >
                  <span className="app-header-bar__logo-wrap">
                    <img
                      src={logo}
                      alt="Venture Capital and Corporate Investments"
                      className="app-header-bar__logo"
                      width={571}
                      height={100}
                      decoding="async"
                      fetchPriority="high"
                    />
                  </span>
                </Link>
              </div>

              {hasCompanySelected && (
                <div className="app-header-bar__split app-header-bar__split--company">
                  <div
                    className="app-header-bar__company-card"
                    title={
                      companyName && companyId
                        ? `${companyName} - ${companyId}`
                        : companyName || companyId || undefined
                    }
                    aria-label={`Selected company: ${companyName || companyId}${companyName && companyId ? ` - ${companyId}` : ""}`}
                  >
                    <span className="app-header-bar__company-dot" aria-hidden />
                    <div className="app-header-bar__company-lines">
                      {companyName ? (
                        <span className="app-header-bar__company-name">
                          {companyName}
                        </span>
                      ) : companyId ? (
                        <span className="app-header-bar__company-name">
                          {companyId}
                        </span>
                      ) : null}
                      {companyName && companyId ? (
                        <>
                          <span className="app-header-bar__company-sep" aria-hidden>
                            -
                          </span>
                          <button
                            type="button"
                            className={`app-header-bar__company-isin${isinCopied ? " is-copied" : ""}`}
                            title={isinCopied ? "ISIN copied" : "Copy ISIN"}
                            aria-label={
                              isinCopied
                                ? `ISIN ${companyId} copied`
                                : `Click to Copy ${companyId}`
                            }
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void copyCompanyIsin();
                            }}
                          >
                            {companyId}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="app-header-bar__cluster app-header-bar__cluster--actions"
              role="toolbar"
              aria-label="Account actions"
            >
              {dispass && (
                <Link
                  to={routePaths.changePassword}
                  className="app-subbar-action app-subbar-action--password"
                  title="Change password"
                >
                  <span className="app-subbar-action__icon" aria-hidden>
                    <FiKey size={16} />
                  </span>
                  <span className="app-subbar-action__label">Password</span>
                </Link>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="app-subbar-action app-subbar-action--logout"
                title="Log out"
              >
                <span className="app-subbar-action__icon" aria-hidden>
                  <FiLogOut size={16} />
                </span>
                <span className="app-subbar-action__label">Logout</span>
              </button>
            </div>
          </div>
        </nav>

        <div className="app-chrome-accent-bar" aria-hidden="true" />

        <HeaderAccentRail
          menuItems={navRailItems}
          menuToggle={
            hasMenus ? (
              <button
                type="button"
                className="app-sidebar-toggle app-sidebar-toggle--on-rail"
                onClick={() => setOpen((prev) => !prev)}
                aria-label={
                  open ? "Collapse navigation menu" : "Expand navigation menu"
                }
                aria-expanded={open}
                title={open ? "Hide menu" : "Show menu"}
              >
                {open ? (
                  <FiChevronsLeft size={16} aria-hidden />
                ) : (
                  <FiMenu size={16} aria-hidden />
                )}
              </button>
            ) : undefined
          }
        />
      </div>

      <div className="app-layout">
        {hasMenus && open && (
          <button
            type="button"
            className="app-sidebar-overlay"
            aria-label="Close navigation menu"
            onClick={closeSidebar}
          />
        )}
        {hasMenus && open && (
          <div className="app-layout__sidebar vcc-sidebar vcc-sidebar--floating">
            <aside
              aria-label="Application menu"
              className="vcc-sidebar__panel flex flex-col flex-1 min-h-0"
            >
              <nav className="menu-nav sidebar-scroll flex-1">
                {sortedMenus.map((item) => (
                  <MenuGroup
                    key={item.id}
                    item={item}
                    isExpanded={expandedId === item.id}
                    onToggle={onToggle}
                    expandedId={expandedId}
                    currentPath={location.pathname}
                    onNavigate={closeSidebar}
                    hasCompanySelected={hasCompanySelected}
                  />
                ))}
              </nav>
            </aside>
          </div>
        )}

        <div className="app-layout__main" onClick={handleMainPanelClick}>
          <div
            className="app-layout__content app-content-frame"
            data-layout-region="outer"
          >
            <div
              className="content app-screen-frame"
              data-layout-region="inner"
            >
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
